import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Lock, ShieldCheck } from "lucide-react";
import { Rocket } from "@/components/Rocket";
import { LOCATIONS } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

const FOOTER_LINKS = [
  ["programs", "/programs", "programs"],
  ["philosophy", "/about", "philosophy"],
  ["locations", "/locations", "locations"],
  ["gallery", "/gallery", "gallery"],
  ["book", "/book", "book-a-tour"],
];

const linkCls = "flex items-center gap-2 text-cream/80 transition-colors hover:text-honey";

const FooterBrand = () => {
  const { t } = useLang();
  return (
    <div className="lg:col-span-5">
      <div className="flex items-center gap-3">
        <Rocket className="h-12 w-12" />
        <div>
          <p className="font-display text-2xl font-semibold tracking-tight">
            Learning <span className="text-honey">&amp;</span> Beyond Academy
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cream/50">{t("footer.city")}</p>
        </div>
      </div>
      <p className="mt-6 max-w-sm text-sm leading-relaxed text-cream/70">{t("footer.blurb")}</p>
      <p className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-honey">
        <Clock className="h-4 w-4" /> {t("hours")}
      </p>
    </div>
  );
};

const FooterLocation = ({ loc }) => {
  const { t } = useLang();
  return (
    <div className="lg:col-span-3" data-testid={`footer-location-${loc.id}`}>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-cream/50">{t(`locations.${loc.id}.name`)}</p>
      <ul className="mt-4 space-y-3 text-sm">
        <li>
          <a href={loc.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid={`footer-directions-${loc.id}`} className={`group ${linkCls} items-start`}>
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {loc.address}
              <ArrowUpRight className="ml-1 inline h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </span>
          </a>
        </li>
        <li>
          <a href={loc.phoneHref} data-testid={`footer-phone-${loc.id}`} className={linkCls}>
            <Phone className="h-4 w-4" /> {loc.phone}
          </a>
        </li>
        <li>
          <a href={`mailto:${loc.email}`} data-testid={`footer-email-${loc.id}`} className={linkCls}>
            <Mail className="h-4 w-4" /> {loc.email}
          </a>
        </li>
      </ul>
    </div>
  );
};

const FooterExplore = () => {
  const { t } = useLang();
  return (
    <div className="lg:col-span-1">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-cream/50">{t("footer.explore")}</p>
      <ul className="mt-4 space-y-3 text-sm">
        {FOOTER_LINKS.map(([key, to, id]) => (
          <li key={to}>
            <Link to={to} data-testid={`footer-link-${id}`} className="text-cream/80 transition-colors hover:text-honey">
              {t(`nav.${key}`)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="grain relative overflow-hidden bg-ink text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <FooterBrand />
          {LOCATIONS.map((loc) => <FooterLocation key={loc.id} loc={loc} />)}
          <FooterExplore />
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-cream/10 pt-8 sm:flex-row sm:items-center">
          <p className="font-mono text-xs text-cream/40">{t("footer.copyright")}</p>
          <div className="flex flex-wrap items-center gap-6">
            <p className="font-mono text-xs text-cream/40">{t("footer.trs")}</p>
            <Link to="/privacy" data-testid="footer-privacy-link" className="flex items-center gap-1.5 font-mono text-xs text-cream/40 transition-colors hover:text-honey">
              <ShieldCheck className="h-3 w-3" /> {t("footer.privacy")}
            </Link>
            <Link to="/admin/login" data-testid="footer-staff-login-link" className="flex items-center gap-1.5 font-mono text-xs text-cream/40 transition-colors hover:text-honey">
              <Lock className="h-3 w-3" /> {t("nav.staff")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
